import { ChakraProvider } from '@chakra-ui/provider';
import type { ColorModeProviderProps } from '@chakra-ui/react';
import '@fontsource/source-sans-pro/300.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';

import { useLayoutEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import {
  initAuth,
  selectImpersonatedUser,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useAuthStore,
  useImpersonationSelector,
} from '@revelio/auth';
import { LoadingGifProvider, userTracking } from '@revelio/core';
import {
  newThemePlotColors,
  resetDarkBreakdownColors as resetDarkReplotsBreakdownColors,
  resetDarkPlotColors as resetDarkReplotsColors,
  resetDarkOtherColor as resetDarkReplotsOtherColor,
  resetBreakdownColors as resetReplotsBreakdownColors,
  resetOtherColor as resetReplotsOtherColor,
  setDarkBreakdownColors as setDarkReplotsBreakdownColors,
  setDarkPlotColors as setDarkReplotsColors,
  setDarkOtherColor as setDarkReplotsOtherColor,
  setBreakdownColors as setReplotsBreakdownColors,
  setPlotColors as setReplotsColors,
  setOtherColor as setReplotsOtherColor,
} from '@revelio/replots';
import { ToastProvider, UITheme } from '@revelio/ui';

import './brands';

import { environment } from './environments/environment';
import { OpenReplayProvider } from './hooks/open-replay';
import { Next } from './next/next';
import {
  BrandProvider,
  useBrand,
  useBrandCssVars,
} from './shared/branding/brand-provider';
import { createBrandedTheme } from './shared/branding/create-branded-theme';
import { ColorModePreferenceSync } from './shared/color-mode/color-mode-preference-sync';
import { AppUpdateToast, MaintenanceToast } from './shared/components';
import { TanstackQueryProvider } from './shared/data-fetch/tanstack-query-provider';
import { RouteAwareUrqlProvider } from './shared/data-fetch/urql-provider';
import { GeographyTaxonomyWarmUp } from './shared/selection-lists/GeographyTaxonomyWarmUp';
import './shared/firebase/firebase-performance';

import { KibanaLoggerProvider } from './shared/hooks/kibana-logger/kibana-logger';
import { installGlobalErrorReporting } from './shared/logging/global-error-reporting';
import { kibanaLogger } from './shared/logging/kibana-logger';
import { SelectionListIdsProvider } from './shared/selection-lists/selection-list-ids-provider';
import {
  UnleashFlags,
  useUnleashUserFlagsReady,
} from './shared/unleash-flags/unleash-flags';

const isWilsonHost =
  typeof window !== 'undefined' &&
  window.location.hostname.toLowerCase().startsWith('wilson.');

initAuth({
  oryKratosRoot: environment.ORY_KRATOS_URL,
  dashMetaRoot: environment.DASH_META_API,
  accountPortalRoot: isWilsonHost
    ? environment.WILSON_ACCOUNT_PORTAL_ROOT
    : environment.ACCOUNT_PORTAL_ROOT,
  accountPortalBackendRoot: environment.ACCOUNT_PORTAL_BACKEND_ROOT,
});

installGlobalErrorReporting(kibanaLogger);

userTracking.init(environment.mixpanelToken, {
  debug: false,
  opt_out_tracking_by_default: !environment.production,
});

const uiThemePortalId = 'ui-portal-root';

function BrandedApp({ children }: { children: React.ReactNode }) {
  const realClientName = useAuthStore((s) => s.user?.client_name);
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);

  const clientName = useMemo(() => {
    // Wilson hostname always renders Wilson branding, regardless of who's
    // logged in. Lets pre-auth visitors and any cross-brand users on
    // wilson.* see the white-label experience.
    if (isWilsonHost) {
      return 'Wilson HR';
    }
    if (isImpersonating && impersonatedUser?.client_name !== undefined) {
      return impersonatedUser.client_name;
    }
    if (overrides?.clientName !== undefined) {
      return overrides.clientName;
    }
    return realClientName;
  }, [realClientName, isImpersonating, impersonatedUser, overrides]);

  return (
    <BrandProvider clientName={clientName}>
      <BrandedChakraProvider>{children}</BrandedChakraProvider>
    </BrandProvider>
  );
}

// Chakra's color mode is no longer used for theming — `data-theme` is the sole
// mechanism, driven by ColorModePreferenceSync — so keep Chakra permanently in
// light mode and never touch localStorage.
type ColorModeManager = NonNullable<ColorModeProviderProps['colorModeManager']>;
const lightModeManager: ColorModeManager = {
  type: 'localStorage',
  ssr: false,
  get: () => 'light',
  set: () => {
    // intentionally a no-op: color mode is never persisted
  },
};

function BrandedChakraProvider({ children }: { children: React.ReactNode }) {
  const brand = useBrand();
  useBrandCssVars();
  const brandedTheme = useMemo(() => createBrandedTheme(brand), [brand]);
  // `flagsReady` alone is not enough: user-targeted flags only become
  // trustworthy after the user context re-fetch (see useUnleashUserFlagsReady).
  const flagsReadyForUser = useUnleashUserFlagsReady();

  useLayoutEffect(() => {
    if (brand.chartColors) {
      setReplotsColors(brand.chartColors);
    } else {
      setReplotsColors(newThemePlotColors);
    }

    const darkChart = brand.darkChartColors ?? brand.chartColors;
    if (darkChart) {
      setDarkReplotsColors(darkChart);
    } else {
      resetDarkReplotsColors();
    }
  }, [brand.chartColors, brand.darkChartColors]);

  useLayoutEffect(() => {
    // Breakdowns inherit the brand plot palette when no override is set; the
    // reset path already lands on the new-theme `newBreakdownColors`.
    const lightBreakdown = brand.breakdownColors ?? brand.chartColors;
    if (lightBreakdown) {
      setReplotsBreakdownColors(lightBreakdown);
    } else {
      resetReplotsBreakdownColors();
    }

    const darkBreakdown =
      brand.darkBreakdownColors ??
      brand.breakdownColors ??
      brand.darkChartColors ??
      brand.chartColors;
    if (darkBreakdown) {
      setDarkReplotsBreakdownColors(darkBreakdown);
    } else {
      resetDarkReplotsBreakdownColors();
    }
  }, [
    brand.breakdownColors,
    brand.darkBreakdownColors,
    brand.chartColors,
    brand.darkChartColors,
  ]);

  useLayoutEffect(() => {
    if (brand.otherColor) {
      setReplotsOtherColor(brand.otherColor);
    } else {
      resetReplotsOtherColor();
    }

    const darkOther = brand.darkOtherColor ?? brand.otherColor;
    if (darkOther) {
      setDarkReplotsOtherColor(darkOther);
    } else {
      resetDarkReplotsOtherColor();
    }
  }, [brand.otherColor, brand.darkOtherColor]);

  return (
    <ChakraProvider
      theme={brandedTheme}
      colorModeManager={lightModeManager}
      portalZIndex={40}
    >
      <ColorModePreferenceSync ready={flagsReadyForUser} />
      <LoadingGifProvider gif={brand.loadingGif}>{children}</LoadingGifProvider>
    </ChakraProvider>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <RouteAwareUrqlProvider>
        <OpenReplayProvider>
          <TanstackQueryProvider>
            <UnleashFlags>
              <SelectionListIdsProvider>
                <UITheme portalId={uiThemePortalId}>
                  <BrandedApp>
                    <ToastProvider>
                      <AppUpdateToast />
                      <GeographyTaxonomyWarmUp />
                      <Next />
                      <MaintenanceToast />
                    </ToastProvider>
                  </BrandedApp>
                  <div id={uiThemePortalId} />
                </UITheme>
              </SelectionListIdsProvider>
            </UnleashFlags>
          </TanstackQueryProvider>
        </OpenReplayProvider>
      </RouteAwareUrqlProvider>
    ),
  },
]);

const rootContainer = document.getElementById('next') as Element;
const root = createRoot(rootContainer);

const renderApp = () => {
  root.render(
    <KibanaLoggerProvider kibanaLogger={kibanaLogger}>
      <RouterProvider router={router} />
    </KibanaLoggerProvider>
  );
};

// Prod builds define __ALLOW_MSW_MOCK_MODE__ as false, dropping this branch and
// the msw chunk it imports. The cookie is matched by exact name on the raw,
// undecoded string, so text inside another cookie's value can never enable it.
if (
  __ALLOW_MSW_MOCK_MODE__ &&
  document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('mswMockDashboard='))
) {
  // Render exactly once: when the worker has started (so the first requests are
  // mocked), when it fails, or after a capped wait, since msw's start() only
  // settles once the worker activates.
  const MSW_START_TIMEOUT_MS = 3000;
  const workerStarted = import('./mocks/browser')
    .then(({ worker }) => worker.start({ onUnhandledRequest: 'bypass' }))
    .catch((error: unknown) => {
      console.error('MSW mock worker failed to start', error);
    });
  const startTimedOut = new Promise<void>((resolve) => {
    setTimeout(() => resolve(), MSW_START_TIMEOUT_MS);
  });
  Promise.race([workerStarted, startTimedOut]).then(renderApp);
} else {
  renderApp();
}
